import { Seo, breadcrumbLd } from "../lib/seo";
import { MaskedLine, FadeIn } from "../components/Reveal";
import { Eyebrow } from "../components/atoms";
import { CONTACT_EMAIL, CONTACT_PHONE_DISPLAY, CONTACT_PHONE_TEL, CORPORATE } from "../data/content";

// Current owner-approved policy, ported verbatim from the live source of truth
// https://www.80metrics.com.au/privacy-policy/ (effective + last updated
// 7 October 2026) — this supersedes the March 2025 repository version.
// One factual migration adjustment, marked below: the old sentence
// "Our website and our client management platform, Mercury, are hosted in
// Australia" is split — Mercury's statement is preserved; this website's
// hosting is described factually for the managed cloud platform (region
// selected at publish). No other wording changes. See LAUNCH_STATUS.md.
const H2 = ({ children, id }) => (
  <h2 id={id} className="mt-14 font-serif text-2xl lg:text-3xl font-medium text-charcoal">
    {children}
  </h2>
);

const P = ({ children }) => (
  <p className="mt-4 text-sm lg:text-base leading-relaxed text-charcoal/70">{children}</p>
);

const UL = ({ items }) => (
  <ul className="mt-4 space-y-2.5 text-sm lg:text-base leading-relaxed text-charcoal/70">
    {items.map((it) => (
      <li key={it} className="flex gap-3">
        <span className="mt-2.5 h-1 w-1 shrink-0 bg-gold rotate-45" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

const ALink = ({ href, children }) => (
  <a href={href} className="text-golddark underline underline-offset-4 hover:text-charcoal transition-colors duration-300">
    {children}
  </a>
);

export default function PrivacyPolicy() {
  return (
    <>
      <Seo
        title="Privacy Policy | 80 Metrics"
        description="How 80 Metrics Property Advisory collects, uses, shares and protects your personal information, and how you can access or correct it."
        jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "Privacy Policy", path: "/privacy-policy" }])]}
      />
      <section className="bg-charcoal text-ivory" data-testid="privacy-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-28 pb-14 lg:pb-20">
          <FadeIn delay={0.1}>
            <Eyebrow dark>Privacy</Eyebrow>
          </FadeIn>
          <h1 className="mt-8 font-serif font-medium text-4xl sm:text-5xl lg:text-7xl leading-[1.04] tracking-tight max-w-4xl">
            <MaskedLine delay={0.25}>Privacy Policy.</MaskedLine>
          </h1>
          <FadeIn delay={0.7}>
            <p className="mt-8 text-sm lg:text-base leading-relaxed text-ivory/60 max-w-xl">
              Effective date <strong className="text-ivory/80 font-semibold">7 October 2026</strong> · Last updated{" "}
              <strong className="text-ivory/80 font-semibold">7 October 2026</strong>
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="bg-white" data-testid="privacy-policy-page">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <P>
            We use personal information to respond to your enquiry, deliver our property advisory services and
            communicate with you. This policy explains what we collect, how we use and protect it, and how you can
            exercise your privacy rights.
          </P>

          <H2 id="who-we-are">Who we are</H2>
          <P>
            80 Metrics Property Advisory is a business name of Key Choice Group Pty Ltd (ABN 27 106 387 786). In this
            policy, "80 Metrics", "we", "us" and "our" mean Key Choice Group Pty Ltd trading as 80 Metrics Property
            Advisory.
          </P>
          <P>
            We provide buyers advocacy, vendor advocacy and property advisory services. This policy applies when you
            visit our website, make an enquiry, book a meeting, subscribe to our updates or engage 80 Metrics. We
            handle personal information in accordance with the Privacy Act 1988 (Cth) and the Australian Privacy
            Principles.
          </P>
          <P>
            Key Choice Group Pty Ltd also operates Key Choice Lending, its mortgage and finance broking business,
            which has its own privacy policy. Although both businesses are operated by the same legal entity, we treat
            information collected through 80 Metrics as being collected for 80 Metrics purposes. We will not use that
            information to provide or market Key Choice Lending services unless you ask us to, agree to an
            introduction, or the use is otherwise authorised or required by law. Because both businesses are operated
            by Key Choice Group Pty Ltd, the company may benefit if you choose to engage Key Choice Lending. We will
            make this clear before any introduction.
          </P>
          <div className="mt-6 border border-charcoal/15 bg-bone px-6 py-5 text-sm leading-relaxed text-charcoal/75" data-testid="privacy-contact-card">
            <p className="font-semibold text-charcoal">Privacy contact: Matthew Clark, Founder and Director of Property Strategy</p>
            <p className="mt-2">
              <ALink href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</ALink> ·{" "}
              <ALink href={`tel:${CONTACT_PHONE_TEL}`}>{CONTACT_PHONE_DISPLAY}</ALink>
            </p>
            <p className="mt-2">{CORPORATE.address}</p>
          </div>

          <H2 id="information-we-collect">Information we collect</H2>
          <P>Depending on how you deal with us, we may collect:</P>
          <UL
            items={[
              "Your name, email address, phone number, postal address and other contact details;",
              "Information you provide in enquiry, booking, subscription and intake forms, including your property goals, preferences and circumstances;",
              "Information relevant to your engagement, such as property details, ownership, budget, your financial position as it relates to your property strategy, and documents you choose to share;",
              "Identity and verification information, which may include details from identification documents, where we need to verify your identity, including where required under anti-money laundering and counter-terrorism financing laws;",
              "Records of our communications, meetings and services, including meeting recordings and transcripts where you have been told a meeting is recorded; and",
              "Website information, such as your device, browser, IP address, pages viewed and how you arrived at our site.",
            ]}
          />
          <P>
            Sometimes personal circumstances, such as health or aged care needs, are relevant to a property decision.
            Health information is sensitive information. We only collect it with your consent and where it is
            reasonably necessary for our services. Please do not send sensitive information through a general enquiry
            form.
          </P>
          <P>
            If you give us information about another person, such as a co-purchaser, partner or family member, please
            let them know and refer them to this policy.
          </P>

          <H2 id="how-we-collect">How we collect information</H2>
          <P>
            We usually collect information directly from you, through forms, email, phone calls, video meetings and
            our booking tool. We may also receive information from a person who refers you to us, professionals you
            ask us to work with (such as your solicitor or accountant), real estate agents and other parties involved
            in your transaction, and publicly available property and market sources. Our website collects some
            technical information automatically through cookies and similar technologies.
          </P>
          <P>
            You can make a general enquiry without identifying yourself, or by using a pseudonym, where practical. If
            you do not provide the information we need, we may be unable to respond fully, verify your identity or
            proceed with an engagement.
          </P>
          <P>
            <strong className="font-semibold text-charcoal">Meeting recordings.</strong> We may record and transcribe
            video meetings so we keep an accurate record of our advice and your instructions. We will tell you at the
            start of a meeting if it is being recorded, and you can ask us not to record.
          </P>

          <H2 id="how-we-use">How we use information</H2>
          <P>We use personal information to:</P>
          <UL
            items={[
              "Respond to enquiries and assess whether our services suit your needs;",
              "Prepare and deliver our property advisory services, including the Understand, Decide and Act stages of our process;",
              "Negotiate on your behalf and deal with other parties as you instruct;",
              "Communicate with you and manage our relationship;",
              "Keep records and meet our legal and regulatory obligations, including under the Estate Agents Act 1980 (Vic) and anti-money laundering and counter-terrorism financing laws; and",
              "Improve our website and services and protect our systems.",
            ]}
          />
          <P>
            <strong className="font-semibold text-charcoal">Technology and AI tools.</strong> We use business versions
            of AI tools to assist with analysis and preparation of drafts. Where client information is processed using
            these tools, we use business arrangements and privacy settings designed to prevent that information being
            used to train publicly available models. A member of our team reviews the resulting work. Decisions and
            recommendations concerning your property strategy are made by our team, not by automated systems.
          </P>
          <P>
            <strong className="font-semibold text-charcoal">Marketing.</strong> We send property insights and
            information about 80 Metrics services by email where we have your consent or are otherwise permitted by
            law. You can unsubscribe at any time using the link in any email or by contacting us. Making an enquiry or
            booking a meeting does not, by itself, subscribe you to marketing emails.
          </P>

          <H2 id="sharing">Who we share information with</H2>
          <P>We may share information with:</P>
          <UL
            items={[
              "Service providers who support our business, including email and document storage, our client relationship and email marketing platform, scheduling, video meetings, website hosting, AI tools, design and IT support;",
              "Our client management platform, Mercury, and its provider;",
              "Parties involved in your matter, as you instruct, such as real estate agents, vendors or their representatives, solicitors and conveyancers, building and pest inspectors, valuers, and your lender or broker;",
              "Key Choice Lending, only as described in “Who we are”; and",
              "Regulators and government agencies, such as the Australian Transaction Reports and Analysis Centre (AUSTRAC), where required or authorised by law.",
            ]}
          />
          <P>
            We do not sell or trade personal information. We limit access to people and providers who need it for an
            authorised purpose.
          </P>

          <H2 id="overseas">Overseas handling</H2>
          <P>
            Some of our service providers and contractors access, process or store personal information outside
            Australia. Based on our current arrangements, this includes the United States (email and document storage,
            our client relationship and email marketing platform, meeting scheduling, video meetings and AI tools),
            the Philippines (design and administrative support) and Indonesia (website development and maintenance).
            Our client management platform, Mercury, is hosted in Australia. This website is operated on a managed
            cloud application platform; the hosting region is selected when the site is published, and depending on
            that configuration, website information may be processed or stored by the platform and its sub-processors
            outside Australia (including the United States, the European Union and India).
          </P>
          <P>
            Before disclosing information overseas, we take reasonable steps, including through contractual and
            security terms, to ensure it is handled consistently with Australian privacy law. We will update this
            policy if our overseas arrangements materially change.
          </P>

          <H2 id="cookies">Cookies and tracking</H2>
          <P>
            Our website uses cookies and similar technologies that are needed for it to work. It may also use tools
            that help us understand how visitors use the site. These tools may collect information such as your IP
            address, device and browser type, pages viewed and how you arrived at our site. Some embedded tools, such
            as our booking calendar and online forms, may set their own cookies.
          </P>
          <P>
            You can manage cookies through your browser settings. Blocking some cookies may affect how the website
            works.
          </P>

          <H2 id="storage">Storage, security and retention</H2>
          <P>
            We store information electronically with reputable providers and use access controls and multi-factor
            authentication to protect it from misuse, interference, loss, and unauthorised access, modification or
            disclosure.
          </P>
          <P>
            We keep information for as long as it is needed for the purposes in this policy and to meet legal
            obligations. Some records must be retained for specified periods under law. For example, relevant AML/CTF
            records are generally required to be retained for at least seven years, with the applicable period
            depending on the type of record and service provided. When information is no longer needed, we take
            reasonable steps to destroy or de-identify it.
          </P>
          <P>
            If a data breach is likely to cause you serious harm, we will notify you and the Office of the Australian
            Information Commissioner (OAIC).
          </P>

          <H2 id="access-correction">Access and correction</H2>
          <P>
            You can ask to access the personal information we hold about you, or to correct information that is
            inaccurate, out of date, incomplete, irrelevant or misleading. There is no charge to make a request. If
            providing access involves significant cost, we may charge a reasonable fee and will tell you before
            proceeding.
          </P>
          <P>
            We aim to respond within 30 days and may need to verify your identity first. If we refuse a request, we
            will explain why in writing, where the law allows, and outline your complaint options.
          </P>

          <H2 id="complaints">Privacy complaints</H2>
          <P>
            If you have a concern about how we have handled your information, contact our privacy contact using the
            details above. Tell us what happened and how you would like it resolved. We will acknowledge your
            complaint within 7 days and aim to respond within 30 days.
          </P>
          <P>
            If you are not satisfied with our response, you can contact the Office of the Australian Information
            Commissioner (OAIC) at <ALink href="https://www.oaic.gov.au/">oaic.gov.au</ALink> or on 1300 363 992.
          </P>

          <div className="mt-14 border border-charcoal/15 bg-bone px-6 lg:px-8 py-6 text-sm leading-relaxed text-charcoal/75" data-testid="privacy-entity-block">
            <p className="font-serif text-xl font-medium text-charcoal">80 Metrics Property Advisory</p>
            <p className="mt-2">Key Choice Group Pty Ltd · ABN 27 106 387 786</p>
            <p className="mt-1">Corporate estate agent’s licence 093462L (Key Choice Group Pty Ltd)</p>
            <p className="mt-1">{CORPORATE.address}</p>
            <p className="mt-2">
              <ALink href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</ALink> ·{" "}
              <ALink href={`tel:${CONTACT_PHONE_TEL}`}>{CONTACT_PHONE_DISPLAY}</ALink>
            </p>
          </div>

          <H2 id="changes">Changes to this policy</H2>
          <P>
            We may update this policy when our practices or legal obligations change. The current version, with its
            effective date, is available at{" "}
            <ALink href="https://www.80metrics.com.au/privacy-policy/">80metrics.com.au/privacy-policy</ALink>.
          </P>
        </div>
      </section>
    </>
  );
}
